import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Badge, Spinner } from "react-bootstrap";
import {
  IconRocket,
  IconArrowRight,
  IconCheck,
  IconInfoCircle,
  IconCreditCard,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import PlanServices from "../../../services/PlanServices";
import CustomerBillingProfileServices from "../../../services/CustomerBillingProfileServices";
import ModalComponent from "../../../jsx/components/Modal";
import NewTermsOfUse from "../../../jsx/components/ModalData/NewTermsOfUse";

const UpgradePlanModal = ({ show, onHide, plan, currentPlan, onConfirm }) => {
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingBillingProfile, setCheckingBillingProfile] = useState(true);
  const [hasBillingProfile, setHasBillingProfile] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState(null);
  const [upgradeError, setUpgradeError] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Preview states
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Verificar perfil de faturamento quando o modal abre
  useEffect(() => {
    if (show && plan) {
      checkBillingProfile();
      // Reset states
      setAcceptedTerms(false);
      setUpgradeResult(null);
      setUpgradeError(null);
      setUpgradePreview(null);
      setPreviewError(null);
    }
  }, [show, plan]);

  const checkBillingProfile = async () => {
    setCheckingBillingProfile(true);
    try {
      const response = await CustomerBillingProfileServices.listCustomerBillingProfiles({
        page_size: 1,
        is_active: true
      });

      const hasProfile = response.data?.results?.length > 0 || response.data?.count > 0;
      setHasBillingProfile(hasProfile);

      // Se tem perfil de faturamento, carregar o preview
      if (hasProfile && plan) {
        loadUpgradePreview();
      }
    } catch (error) {
      console.error("Erro ao verificar perfil de faturamento:", error);
      setHasBillingProfile(false);
    } finally {
      setCheckingBillingProfile(false);
    }
  };

  const loadUpgradePreview = async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const response = await PlanServices.previewUpgrade(plan.id);
      if (response.data) {
        setUpgradePreview(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar preview do upgrade:", error);
      const errorInfo = PlanServices.processPlanError(error);
      setPreviewError(errorInfo.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  if (!plan || !currentPlan) {
    return null;
  }

  const handleConfirm = async () => {
    if (!acceptedTerms) return;

    setLoading(true);
    setUpgradeError(null);

    try {
      const response = await PlanServices.upgradePlan(plan.id);

      if (response.data?.success) {
        // Substituir tokens para refletir o novo plano imediatamente
        if (response.data.tokens) {
          const { access, refresh } = response.data.tokens;
          if (access) {
            setCookie(undefined, "token", access, {
              maxAge: 60 * 60 * 24 * 14,
              path: "/",
              sameSite: "Lax",
            });

            // Atualizar plan_id no sessionStorage
            const decoded = jwt_decode(access);
            if (decoded.plan_id) {
              sessionStorage.setItem("plan_id", decoded.plan_id);
            }
          }
          if (refresh) {
            setCookie(undefined, "refresh_token", refresh, {
              maxAge: 60 * 60 * 24 * 14,
              path: "/",
              sameSite: "Lax",
            });
          }
        }

        setUpgradeResult(response.data);
      }
    } catch (error) {
      const errorInfo = PlanServices.processPlanError(error);
      setUpgradeError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToBilling = () => {
    onHide();
    navigate("/billing");
  };

  const handleClose = () => {
    // Se houve upgrade bem-sucedido, recarregar para aplicar novo token
    if (upgradeResult) {
      if (onConfirm) {
        onConfirm(plan, upgradeResult);
      }
      window.location.reload();
      return;
    }
    setUpgradeResult(null);
    setUpgradeError(null);
    setUpgradePreview(null);
    setPreviewError(null);
    onHide();
  };

  // Renderizar estado de carregamento inicial
  if (checkingBillingProfile) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="mb-0">Verificando perfil de faturamento...</p>
        </Modal.Body>
      </Modal>
    );
  }

  // Renderizar resultado do upgrade com sucesso
  if (upgradeResult) {
    const formatDate = (dateString) => {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    };

    return (
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-0 bg-success">
          <Modal.Title className="d-flex align-items-center text-white">
            <IconCheck size={28} className="me-2" />
            Upgrade Realizado com Sucesso!
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="success" className="mb-4">
            <div className="d-flex align-items-center">
              <IconRocket size={24} className="me-3 flex-shrink-0" />
              <div>
                <strong>{upgradeResult.message}</strong>
                <p className="mb-0 mt-1">
                  Seu plano foi alterado de{" "}
                  <strong>{upgradeResult.old_plan?.name}</strong> para{" "}
                  <strong>{upgradeResult.new_plan?.name}</strong>.
                </p>
              </div>
            </div>
          </Alert>

          {/* Resumo da transação */}
          <div className="bg-light rounded p-4 mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <IconCreditCard size={18} className="me-2" />
              Resumo da Cobrança
            </h6>
            <div className="row g-3">
              {/* Plano anterior */}
              <div className="col-6">
                <small className="text-muted d-block">Plano anterior</small>
                <strong>{upgradeResult.old_plan?.name}</strong>
                <span className="text-muted ms-1">
                  ({PlanServices.formatCurrency(upgradeResult.old_plan?.value)}
                  /mês)
                </span>
              </div>

              {/* Novo plano */}
              <div className="col-6">
                <small className="text-muted d-block">Novo plano</small>
                <strong className="text-primary">
                  {upgradeResult.new_plan?.name}
                </strong>
                <span className="text-muted ms-1">
                  ({PlanServices.formatCurrency(upgradeResult.new_plan?.value)}
                  /mês)
                </span>
              </div>

              {/* Crédito proporcional */}
              {upgradeResult.calculation && (
                <>
                  <div className="col-6">
                    <small className="text-muted d-block">
                      Dias restantes do plano anterior
                    </small>
                    <strong>
                      {upgradeResult.calculation.days_remaining} dias
                    </strong>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">
                      Crédito proporcional aplicado
                    </small>
                    <strong className="text-success">
                      -{" "}
                      {PlanServices.formatCurrency(
                        upgradeResult.calculation.credit,
                      )}
                    </strong>
                  </div>
                </>
              )}

              {/* Valor cobrado */}
              {upgradeResult.upgrade_charge && (
                <div className="col-12 border-top pt-3 mt-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted d-block">
                        Valor cobrado hoje
                      </small>
                      <strong className="fs-5 text-primary">
                        {PlanServices.formatCurrency(
                          upgradeResult.upgrade_charge.value,
                        )}
                      </strong>
                    </div>
                    <Badge bg="success" className="px-3 py-2">
                      <IconCheck size={14} className="me-1" />
                      Pagamento Confirmado
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informações da nova assinatura */}
          {upgradeResult.subscription && (
            <div className="bg-primary bg-opacity-10 rounded p-4 mb-4">
              <h6 className="mb-3 text-white">Sua Nova Assinatura</h6>
              <div className="row g-2">
                <div className="col-6">
                  <small className="text-muted d-block">Valor mensal</small>
                  <strong>
                    {PlanServices.formatCurrency(
                      upgradeResult.subscription.monthly_value,
                    )}
                  </strong>
                </div>
                <div className="col-6">
                  <small className="d-block">
                    Próximo vencimento
                  </small>
                  <strong>
                    {formatDate(upgradeResult.subscription.next_due_date)}
                  </strong>
                </div>
              </div>
            </div>
          )}

          <Alert variant="info" className="mb-0">
            <IconInfoCircle size={18} className="me-2" />
            Sua nova assinatura foi ativada. Aproveite todos os recursos do
            plano {upgradeResult.new_plan?.name}!
          </Alert>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="success" onClick={handleClose} size="lg">
            <IconCheck size={18} className="me-2" />
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar mensagem de perfil de faturamento não encontrado
  if (!hasBillingProfile) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <IconCreditCard size={28} className="text-warning me-2" />
            Perfil de Faturamento Necessário
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="warning" className="mb-4">
            <div className="d-flex align-items-start">
              <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
              <div>
                <strong>Atenção!</strong>
                <p className="mb-0 mt-2">
                  Para realizar o upgrade do seu plano, você precisa primeiro cadastrar
                  um perfil de faturamento com os dados de pagamento.
                </p>
              </div>
            </div>
          </Alert>

          <div className="text-center py-3">
            <IconCreditCard size={64} className="text-muted mb-3" />
            <h5>Cadastre seu perfil de faturamento</h5>
            <p className="text-muted mb-0">
              Adicione um cartão de crédito ou outro meio de pagamento para continuar.
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleGoToBilling}>
            <IconCreditCard size={18} className="me-2" />
            Ir para Faturamento
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar modal de confirmação de upgrade
  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <IconRocket size={28} className="text-primary me-2" />
          Upgrade de Plano
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Loading do preview */}
        {loadingPreview && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="mb-0 text-muted">Calculando valores do upgrade...</p>
          </div>
        )}

        {/* Erro do preview */}
        {previewError && (
          <Alert variant="danger" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {previewError}
          </Alert>
        )}

        {/* Preview do upgrade */}
        {upgradePreview && !loadingPreview && (
          <>
            {/* Resumo visual: Plano atual -> Novo plano */}
            <div className="d-flex align-items-center justify-content-center mb-4 p-4 bg-light rounded">
              <div className="text-center">
                <Badge bg="secondary" className="px-3 py-2 fs-6">
                  {upgradePreview.current_plan?.name}
                </Badge>
                <div className="mt-2 small text-muted">
                  {PlanServices.formatCurrency(
                    upgradePreview.current_plan?.value,
                  )}
                  /mês
                </div>
              </div>
              <IconArrowRight size={32} className="mx-4 text-primary" />
              <div className="text-center">
                <Badge bg="primary" className="px-3 py-2 fs-6">
                  {upgradePreview.new_plan?.name}
                </Badge>
                <div className="mt-2 small text-muted">
                  {PlanServices.formatCurrency(upgradePreview.new_plan?.value)}
                  /mês
                </div>
              </div>
            </div>

            {/* Detalhes do cálculo */}
            <div className="bg-light rounded p-4 mb-4">
              <h6 className="mb-3 d-flex align-items-center">
                <IconCreditCard size={18} className="me-2" />
                Resumo do Upgrade
              </h6>
              <div className="row g-3">
                {upgradePreview.calculation && (
                  <>
                    <div className="col-6">
                      <small className="text-muted d-block">
                        Dias restantes do plano atual
                      </small>
                      <strong>
                        {upgradePreview.calculation.days_remaining} dias
                      </strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">
                        Crédito proporcional
                      </small>
                      <strong className="text-success">
                        -{" "}
                        {PlanServices.formatCurrency(
                          upgradePreview.calculation.credit,
                        )}
                      </strong>
                    </div>
                  </>
                )}

                {upgradePreview.summary && (
                  <>
                    <div className="col-12 border-top pt-3 mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Desconto aplicado:</span>
                        <strong className="text-success">
                          -{" "}
                          {PlanServices.formatCurrency(
                            upgradePreview.summary.discount_applied,
                          )}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-white">Cobrança hoje:</span>
                        <strong className="fs-5 text-primary">
                          {PlanServices.formatCurrency(
                            upgradePreview.summary.today_charge,
                          )}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                        <span className="text-white">
                          Valor mensal após upgrade:
                        </span>
                        <strong>
                          {PlanServices.formatCurrency(
                            upgradePreview.summary.monthly_after,
                          )}
                          /mês
                        </strong>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recursos adicionais disponíveis (Business legado e planos WhiteLabel) */}
            {[5, 9, 10, 11].includes(plan.id) && (
              <Alert variant="info" className="d-flex align-items-start mb-4">
                <IconInfoCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                <div>
                  <strong>Bom saber!</strong> Com o plano {plan.name}, você terá
                  acesso a maiores limites por subcontas, e terá a opção de
                  contratar recursos adicionais conforme sua necessidade.
                  <strong> Também está incluso nossa versão white label</strong>
                  , qual você deve entrar em contato com o suporte para realizar
                  a ativação se você já tiver um domínio disponível.
                </div>
              </Alert>
            )}
          </>
        )}

        {/* Fallback se não tiver preview mas tiver billing profile */}
        {!upgradePreview && !loadingPreview && !previewError && (
          <div className="d-flex align-items-center justify-content-center mb-4 p-4 bg-light rounded">
            <div className="text-center">
              <Badge bg="secondary" className="px-3 py-2 fs-6">
                {currentPlan.name}
              </Badge>
              {currentPlan.price && (
                <div className="mt-2 small text-muted">
                  {PlanServices.formatCurrency(currentPlan.price)}/mês
                </div>
              )}
            </div>
            <IconArrowRight size={32} className="mx-4 text-primary" />
            <div className="text-center">
              <Badge bg="primary" className="px-3 py-2 fs-6">
                {plan.name}
              </Badge>
              {plan.prices && plan.prices.length > 0 && (
                <div className="mt-2 small text-muted">
                  {PlanServices.formatCurrency(plan.prices[0].value)}/mês
                </div>
              )}
            </div>
          </div>
        )}

        {/* Erro de upgrade */}
        {upgradeError && (
          <Alert variant="danger" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {upgradeError}
          </Alert>
        )}

        {/* Aceite dos termos */}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="accept-terms"
            label={
              <span>
                Li e aceito os{" "}
                <span
                  style={{ color: 'var(--primary, #3b82f6)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTermsModal(true);
                  }}
                >
                  termos de uso
                </span>
              </span>
            }
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
        </Form.Group>

        {/* Modal de Termos de Uso */}
        <ModalComponent
          props={{
            title: "Termos de Uso",
            content: <NewTermsOfUse />,
            showModal: showTermsModal,
            setShowModal: setShowTermsModal,
            size: "xl",
          }}
        />
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={
            !acceptedTerms ||
            loading ||
            loadingPreview ||
            !upgradePreview?.can_upgrade
          }
          className="d-flex align-items-center"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processando...
            </>
          ) : (
            <>
              <IconRocket size={18} className="me-2" />
              Confirmar Upgrade
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpgradePlanModal;
